import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { UITenantEntity, TenantEntityResponse } from '../services/types';
import { tenantEntitiesService } from '../services/tenantEntities.service';

type EntityType = 'sit' | 'label' | 'policy';

interface EntityTypeState {
  entities: UITenantEntity[];
  loading: boolean;
  error?: string;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  currentTenantId?: string;
}

interface TenantEntitiesState {
  sits: EntityTypeState;
  labels: EntityTypeState;
  policies: EntityTypeState;
}

const initialEntityState: EntityTypeState = {
  entities: [],
  loading: false,
  pageNumber: 1,
  pageSize: 5,
  totalPages: 0,
  hasNextPage: false,
};

const initialState: TenantEntitiesState = {
  sits: { ...initialEntityState },
  labels: { ...initialEntityState },
  policies: { ...initialEntityState },
};

// Async thunks for fetching tenant entities by type
export const fetchTenantSits = createAsyncThunk<
  TenantEntityResponse,
  {
    tenantId: string;
    pageNumber?: number;
  }
>('tenantEntities/fetchSits', async ({ tenantId, pageNumber }) => {
  const response = await tenantEntitiesService.list(tenantId, 'sit', pageNumber);
  return response;
});

export const fetchTenantLabels = createAsyncThunk<
  TenantEntityResponse,
  {
    tenantId: string;
    pageNumber?: number;
  }
>('tenantEntities/fetchLabels', async ({ tenantId, pageNumber }) => {
  const response = await tenantEntitiesService.list(tenantId, 'label', pageNumber);
  return response;
});

export const fetchTenantPolicies = createAsyncThunk<
  TenantEntityResponse,
  {
    tenantId: string;
    pageNumber?: number;
  }
>('tenantEntities/fetchPolicies', async ({ tenantId, pageNumber }) => {
  const response = await tenantEntitiesService.list(tenantId, 'policy', pageNumber);
  return response;
});

// Backward compatibility - deprecated, use specific entity type thunks
export const fetchTenantEntities = createAsyncThunk<
  TenantEntityResponse,
  {
    tenantId: string;
    entityType: EntityType;
    pageNumber?: number;
  }
>('tenantEntities/fetch', async ({ tenantId, entityType, pageNumber }) => {
  const response = await tenantEntitiesService.list(tenantId, entityType, pageNumber);
  return response;
});

const tenantEntitiesSlice = createSlice({
  name: 'tenantEntities',
  initialState,
  reducers: {
    // SITs actions
    clearTenantSits: (state) => {
      state.sits = { ...initialEntityState };
    },
    setTenantSitsPage: (state, action: PayloadAction<number>) => {
      state.sits.pageNumber = Math.max(1, action.payload);
    },
    
    // Labels actions
    clearTenantLabels: (state) => {
      state.labels = { ...initialEntityState };
    },
    setTenantLabelsPage: (state, action: PayloadAction<number>) => {
      state.labels.pageNumber = Math.max(1, action.payload);
    },
    
    // Policies actions
    clearTenantPolicies: (state) => {
      state.policies = { ...initialEntityState };
    },
    setTenantPoliciesPage: (state, action: PayloadAction<number>) => {
      state.policies.pageNumber = Math.max(1, action.payload);
    },

    // Backward compatibility - deprecated actions
    clearEntities: (state) => {
      // state.entities = [];
      // state.error = undefined;
      // state.pageNumber = 1;
      // state.totalPages = 0;
      // state.hasNextPage = false;
      state.sits = { ...initialEntityState };
      state.labels = { ...initialEntityState };
      state.policies = { ...initialEntityState };
    },
    setPageNumber: (state, action: PayloadAction<number>) => {
    },
  },
  extraReducers: (builder) => {
    builder
      // SITs thunk handlers
      .addCase(fetchTenantSits.pending, (state, action) => {
        state.sits.loading = true;
        state.sits.error = undefined;
        state.sits.currentTenantId = action.meta.arg.tenantId;
      })
      .addCase(fetchTenantSits.fulfilled, (state, action) => {
        state.sits.loading = false;
        state.sits.entities = action.payload.entities;
        state.sits.pageNumber = action.payload.pageNumber;
        state.sits.pageSize = action.payload.pageSize;
        state.sits.totalPages = action.payload.totalPages;
        state.sits.hasNextPage = action.payload.hasNextPage;
      })
      .addCase(fetchTenantSits.rejected, (state, action) => {
        state.sits.loading = false;
        state.sits.error = action.error.message || 'Failed to load tenant SITs';
      })

      // Labels thunk handlers
      .addCase(fetchTenantLabels.pending, (state, action) => {
        state.labels.loading = true;
        state.labels.error = undefined;
        state.labels.currentTenantId = action.meta.arg.tenantId;
      })
      .addCase(fetchTenantLabels.fulfilled, (state, action) => {
        state.labels.loading = false;
        state.labels.entities = action.payload.entities;
        state.labels.pageNumber = action.payload.pageNumber;
        state.labels.pageSize = action.payload.pageSize;
        state.labels.totalPages = action.payload.totalPages;
        state.labels.hasNextPage = action.payload.hasNextPage;
      })
      .addCase(fetchTenantLabels.rejected, (state, action) => {
        state.labels.loading = false;
        state.labels.error = action.error.message || 'Failed to load tenant labels';
      })

      // Policies thunk handlers
      .addCase(fetchTenantPolicies.pending, (state, action) => {
        state.policies.loading = true;
        state.policies.error = undefined;
        state.policies.currentTenantId = action.meta.arg.tenantId;
      })
      .addCase(fetchTenantPolicies.fulfilled, (state, action) => {
        state.policies.loading = false;
        state.policies.entities = action.payload.entities;
        state.policies.pageNumber = action.payload.pageNumber;
        state.policies.pageSize = action.payload.pageSize;
        state.policies.totalPages = action.payload.totalPages;
        state.policies.hasNextPage = action.payload.hasNextPage;
      })
      .addCase(fetchTenantPolicies.rejected, (state, action) => {
        state.policies.loading = false;
        state.policies.error = action.error.message || 'Failed to load tenant policies';
      })

      // Backward compatibility - deprecated fetchTenantEntities
      .addCase(fetchTenantEntities.pending, (state, action) => {
        const entityType = action.meta.arg.entityType;
        state[`${entityType}s` as keyof TenantEntitiesState].loading = true;
        state[`${entityType}s` as keyof TenantEntitiesState].error = undefined;
        state[`${entityType}s` as keyof TenantEntitiesState].currentTenantId = action.meta.arg.tenantId;
      })
      .addCase(fetchTenantEntities.fulfilled, (state, action) => {
        const entityType = action.meta.arg.entityType;
        const targetState = state[`${entityType}s` as keyof TenantEntitiesState];
        targetState.loading = false;
        targetState.entities = action.payload.entities;
        targetState.pageNumber = action.payload.pageNumber;
        targetState.pageSize = action.payload.pageSize;
        targetState.totalPages = action.payload.totalPages;
        targetState.hasNextPage = action.payload.hasNextPage;
      })
      .addCase(fetchTenantEntities.rejected, (state, action) => {
        const entityType = action.meta.arg.entityType;
        const targetState = state[`${entityType}s` as keyof TenantEntitiesState];
        targetState.loading = false;
        targetState.error = action.error.message || `Failed to load tenant ${entityType}s`;
      });
  },
});

export const { 
  clearTenantSits, 
  setTenantSitsPage,
  clearTenantLabels,
  setTenantLabelsPage,
  clearTenantPolicies,
  setTenantPoliciesPage,
  // Deprecated - for backward compatibility
  clearEntities, 
  setPageNumber 
} = tenantEntitiesSlice.actions;

export default tenantEntitiesSlice.reducer;